import { Method } from 'axios';

export interface BookingAPIEndpoint {
    url: string;
    method: Method;
}

const BookingAPIBasePath = '/bookingAPI';
// cf-turnstile-response
export const HEADER_CF_TURNSTILE_RESPONSE = 'cf-turnstile-response';
// Authorization
export const HEADER_AUTHORIZATION = 'Authorization';

const merchantAreasPath = {
    init: (merchantId: string): BookingAPIEndpoint => ({
        url: `${BookingAPIBasePath}/merchants/${merchantId}/merchantAreas`,
        method: 'POST'
    }),
    query: (merchantId: string): BookingAPIEndpoint => ({
        url: `${BookingAPIBasePath}/merchants/${merchantId}/merchantAreas`,
        method: 'GET'
    }),
    updateMerchantAreas: (merchantId: string): BookingAPIEndpoint => ({
        url: `${BookingAPIBasePath}/merchants/${merchantId}/merchantAreas`,
        method: 'PUT'
    })
};

const merchantAreaBackupsPath = {
    query: (merchantId: string): BookingAPIEndpoint => ({
        url: `${BookingAPIBasePath}/merchants/${merchantId}/merchantArea/backups`,
        method: 'GET'
    }),
    rollback: (merchantId: string, backupId: string): BookingAPIEndpoint => ({
        url: `${BookingAPIBasePath}/merchants/${merchantId}/merchantArea/backups/${backupId}/rollback`,
        method: 'POST'
    }),
    delete: (merchantId: string, backupId: string): BookingAPIEndpoint => ({
        url: `${BookingAPIBasePath}/merchants/${merchantId}/merchantArea/backups/${backupId}`,
        method: 'DELETE'
    })
};

export const bookingAPIPath = {
    utils: {
        createShortLink: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/utils/shortLink`,
            method: 'POST'
        }),
        smsReport: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/utils/sms/report`,
            method: 'GET'
        }),
        smsReportDetails: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/utils/sms/report/details`,
            method: 'GET'
        })
    },
    message: {
        createMessage: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/message/admin`,
            method: 'POST'
        }),
        queryMessages: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/message/admin`,
            method: 'GET'
        }),
        updateMessage: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/message/admin`,
            method: 'PUT'
        })
    },
    users: {
        queryUserRecords: (): BookingAPIEndpoint => ({
            method: 'GET',
            url: `${BookingAPIBasePath}/users/recordList`
        })
    },
    google: {
        queryGooglePublicSettings: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/google/${merchantId}/settings`,
            method: 'GET'
        }),
        queryGoogleSettings: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/google/${merchantId}/settings`,
            method: 'GET'
        }),
        updateGoogleSettings: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/google/${merchantId}/settings`,
            method: 'PUT'
        }),
        updateGoogleDailyFeeds: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/google/runManualFeedsUpdate`,
            method: 'POST'
        }),
        queryGoogleDailyFeeds: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/google/dailyFeeds`,
            method: 'GET'
        }),
        replaceGoogleAvailability: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/google/realTimeUpdate/availability`,
            method: 'POST'
        })
        
    },
    merchant: {
        query: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/merchants/${merchantId}`,
            method: 'GET'
        }),
        areas: merchantAreasPath,
        areaBackups: merchantAreaBackupsPath,
        queryMerchantShortLinks: (merchantId: string): BookingAPIEndpoint => {
            return {
                // url: "merchants/:merchantId/shortLink",
                url: `${BookingAPIBasePath}/merchants/${merchantId}/shortLinks`,
                method: 'GET'
            };
        }
    },
    public: {
        updateBookingByCustomer: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/booking/${merchantId}`,
            method: 'PUT'
        }),
        createBookingByCustomer: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/booking/${merchantId}`,
            method: 'POST'
        }),
        queryPublicBooking: (merchantId: string, yearStr: string, bookingNumber: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/booking/${merchantId}/${yearStr}/${bookingNumber}`,
            method: 'GET'
        }),
        queryBookingAvailableTime: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/${merchantId}/availableBookingTime`,
            method: 'GET'
        }),
        queryMerchant: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/merchants/${merchantId}`,
            method: 'GET'
        }),
        submitBookingOnboarding: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/public/booking/onboarding`,
            method: 'POST'
        }),
        downloadCalendar: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}/calendar`,
            method: 'GET'
        })
    },
    booking: {
        queryByStaff: (merchantId: string, yearStr: string, bookingNumber: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}/${yearStr}/${bookingNumber}`,
            method: 'GET'
        }),
        updateBookingDepositStatus: (merchantId: string, yearStr: string, bookingNumber: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}/${yearStr}/${bookingNumber}/deposit`,
            method: 'POST'
        }),
        updateByStaff: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}`,
            method: 'PUT'
        }),
        updateBookingSettings: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}/settings`,
            method: 'PUT'
        }),
        queryBookingSettings: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}/settings`,
            method: 'GET'
        }),
        queryBookingList: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}`,
            method: 'GET'
        }),
        queryBookingAvailableTime: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}/availableBookingTime`,
            method: 'GET'
        }),
        queryBookingOnboardingSubmissions: (): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/onboarding`,
            method: 'GET'
        }),
        queryBookingOnboardingSubmission: (submissionId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/onboarding/${submissionId}`,
            method: 'GET'
        }),
        updateBookingOnboardingSubmission: (submissionId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/onboarding/${submissionId}`,
            method: 'PUT'
        }),
        createBookingByStaff: (merchantId: string): BookingAPIEndpoint => ({
            url: `${BookingAPIBasePath}/booking/${merchantId}`,
            method: 'POST'
        })
    },
    plans: {
        // Admin endpoints
        adminCreate: (): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/admin`, method: 'POST' }),
        adminList: (): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/admin`, method: 'GET' }),
        adminGetById: (planId: string): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/admin/${planId}`, method: 'GET' }),
        adminUpdate: (planId: string): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/admin/${planId}`, method: 'PUT' }),
        adminListSubscriptions: (): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/admin/subscriptions`, method: 'GET' }),
        // Merchant endpoints
        merchantList: (): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/merchant`, method: 'GET' }),
        merchantGetSubscription: (): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/merchant/subscription`, method: 'GET' }),
        merchantSelectPlan: (): BookingAPIEndpoint => ({ url: `${BookingAPIBasePath}/plans/merchant/subscription`, method: 'POST' })
    }
};
